import { z } from "zod";
import { baseEntitySchema } from "./base";
import { UserRoles } from "./user";

// Enum for audit actions
export enum AuditLogAction {
  CREATE = "create",
  UPDATE = "update",
  DELETE = "delete",
}

// Schema for field changes (before/after values)
export const fieldChangeSchema = z.object({
  from: z.any().nullable(),
  to: z.any().nullable(),
});

// Schema for user information
export const auditLogUserSchema = z.object({
  id: z.string(),
  email: z.string().optional(),
  isAdmin: z.boolean().default(true),
  roles: z.array(z.nativeEnum(UserRoles)).optional(),
});

export const auditLogPerformedByTypeSchema = z.enum(["user", "system"]);
export type AuditLogPerformedByType = z.infer<
  typeof auditLogPerformedByTypeSchema
>;

export const auditLogEntityTypeSchema = z.enum([
  "inventory",
  "purchase_order",
  "sales_order",
  "invoice",
  "payment",
  "stock_check",
]);
export type AuditLogEntityType = z.infer<typeof auditLogEntityTypeSchema>;

// Main audit log data schema
export const auditLogDataSchema = z.object({
  // Action performed
  action: z.nativeEnum(AuditLogAction),

  // Collection and document information
  collection: z.string().min(1),
  documentId: z.string().min(1),
  eventType: z
    .enum([
      "document_change",
      "sensitive_view",
      "sensitive_reveal",
      "export",
      "login",
    ])
    .default("document_change"),
  objectType: z.string().optional(),
  objectId: z.string().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),

  // User who performed the action
  user: auditLogUserSchema,
  performedByType: auditLogPerformedByTypeSchema.default("user"),

  // Field changes (for update operations)
  changes: z.record(z.string(), fieldChangeSchema).optional(),

  deletedData: z.record(z.string(), z.any()).optional(),
  createData: z.record(z.string(), z.any()).optional(),
  inventoryId: z.string().optional(),
  entityType: auditLogEntityTypeSchema.optional(),
  entityId: z.string().optional(),
  actionType: z.string().min(1).optional(),
  summary: z.string().optional(),
});

export type AuditLogData = z.infer<typeof auditLogDataSchema>;
export type FieldChange = z.infer<typeof fieldChangeSchema>;
export type AuditLogUserContext = z.infer<typeof auditLogUserSchema>;

// Complete audit log schema with base entity fields
export const auditLogSchema = baseEntitySchema.and(auditLogDataSchema);
export type AuditLog = z.infer<typeof auditLogSchema>;
